<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px"/>
  </div>
  <alert-badge
      v-else-if="error.title"
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />
  <el-form
      v-else
      @submit.prevent="validateSubmit('commissionForm')"
      novalidate
      :model="commission"
      :rules="rules"
      label-position="top"
      ref="commissionForm"
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('commissionForm')"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('commissionForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('moneyCollectorCommissionInfo') }}</h1>

      <el-form-item :label="t('name')" prop="name" class="input-label">
        <el-input
            id="name"
            v-model="commission.name"
            :placeholder="t('name')"
            maxlength="255"
            show-word-limit
            clearable
            required
        >
        </el-input>
      </el-form-item>

      <el-form-item :label="t('stores')" prop="stores" class="input-label">
        <el-select
            class="w-full"
            filterable
            clearable
            v-model="commission.stores"
            multiple
            :placeholder="t('stores')"
            :class="{'select-all': commission.includeAllStores}"
        >
          <template #header>
            <el-checkbox
                v-model="commission.includeAllStores"
                @change="() => {
                     commission.stores = [];
                }"
            >
              {{ t('all') }}
            </el-checkbox>
          </template>
          <template #prefix>
             <span v-if="commission.includeAllStores">
               {{ t('all') }}
             </span>
          </template>
          <el-option
              v-for="item in locations"
              :disabled="commission.includeAllStores"
              :key="item.id"
              :label="$ctx.locale === 'ar' ? item.arName : item.enName"
              :value="item.id"
          />
        </el-select>
      </el-form-item>

      <div class="flex gap-2 items-center">
        <h1>{{ t('rules') }}</h1>
        <button
            class="px-3 rounded-lg bg-blue-200 hover:bg-blue-300 active:bg-blue-400 text-xl"
            type="button" @click="addRule">+
        </button>
      </div>

      <draggable
          tag="ul"
          :list="commission.rules"
          class="list-group"
          handle=".handle"
          item-key="order"
          @change="reorder"
      >
        <template #item="{ element, index }">
          <li class="p-2 my-2 flex items-center gap-2">
            <div class="handle cursor-pointer">
              <svg width="22" height="12" viewBox="0 0 22 12" fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <line x1="1" y1="1" x2="21" y2="1" stroke="#818181"
                      stroke-width="2" stroke-linecap="round"/>
                <line x1="1" y1="6" x2="21" y2="6" stroke="#818181"
                      stroke-width="2" stroke-linecap="round"/>
                <line x1="1" y1="11" x2="21" y2="11" stroke="#818181"
                      stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>

            <!-- From Day -->
            <div class="w-full flex items-center gap-2">
              <span class="text-sm whitespace-nowrap">{{ t('from') }}</span>
              <el-input-number
                  class="w-full custom-input-number"
                  v-model="element.fromDay"
                  :step="1"
                  :min="0"
                  :precision="0"
                  :controls="false"
                  :placeholder="t('fromDay')"
              />
              <span class="text-sm whitespace-nowrap">{{ t('days') }}</span>
            </div>

            <!-- To Day -->
            <div class="w-full flex items-center gap-2">
              <span class="text-sm whitespace-nowrap">{{ t('to') }}</span>
              <el-input-number
                  class="w-full custom-input-number"
                  v-model="element.toDay"
                  :step="1"
                  :min="0"
                  :precision="0"
                  :controls="false"
                  :placeholder="t('toDay')"
              />
              <span class="text-sm whitespace-nowrap">{{ t('days') }}</span>
            </div>

            <!-- Type (Amount or Percentage) -->
            <div class="w-full">
              <el-select
                  class="w-full"
                  filterable
                  clearable
                  v-model="element.type"
                  :placeholder="t('type')"
              >
                <el-option
                    v-for="item in ['amount', 'percentage']"
                    :key="item"
                    :label="t(item)"
                    :value="item"
                />
              </el-select>
            </div>

            <!-- Commission Value (Amount) -->
            <div v-if="element.type == 'amount'" class="w-full">
              <money-input
                  id="commission-amount"
                  :currency="$ctx.currency"
                  v-model="element.amount"
              />
            </div>

            <!-- Commission Value (Percentage) -->
            <div v-if="element.type == 'percentage'" class="w-full flex items-center gap-2">
              <span>%</span>
              <el-input-number
                  class="w-full custom-input-number"
                  v-model="element.amount"
                  :step="0.1"
                  :min="0"
                  :max="100"
                  :precision="1"
                  :controls="false"
              />
            </div>

            <!-- Placeholder if no type selected -->
            <div v-if="!element.type" class="w-full">
              <el-input disabled :placeholder="t('commissionValue')"></el-input>
            </div>

            <button type="button" @click="deleteRule(element, index)">
              <i class="fa fa-times close"></i>
            </button>
          </li>
        </template>
      </draggable>

      <el-form-item :label="t('employees')" prop="employees" class="input-label">
        <el-select
            class="w-full"
            filterable
            clearable
            v-model="commission.employees"
            multiple
            :placeholder="t('employees')"
            :class="{'select-all': commission.includeAllEmployees}"
        >
          <template #header>
            <el-checkbox
                v-model="commission.includeAllEmployees"
                @change="() => {
                    commission.employees = [];
                }"
            >
              {{ t('all') }}
            </el-checkbox>
          </template>
          <template #prefix>
              <span v-if="commission.includeAllEmployees">
                {{ t('all') }}
              </span>
          </template>
          <el-option
              v-for="item in moneyCollectors"
              :disabled="commission.includeAllEmployees"
              :key="item.id"
              :label="item.name"
              :value="item.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="t('period')" prop="period" class="input-label">
        <el-select
            class="w-full"
            filterable
            clearable
            v-model="commission.period"
            :placeholder="t('period')"
        >
          <el-option
              v-for="item in periods"
              :key="item"
              :label="t(item)"
              :value="item"
          />
        </el-select>
      </el-form-item>

      <div v-if="commission.period == 'date_range'" class="flex gap-2">
        <el-form-item :label="t('from')" prop="fromDate" class="input-label w-full">
          <el-date-picker
              style="width: 100%;"
              v-model="commission.fromDate"
              type="date"
              :placeholder="t('from')"
              size="large"
              :popper-class="$ctx.getDir() === 'rtl' ? 'rtl' : ''"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item :label="t('to')" prop="toDate" class="input-label w-full">
          <el-date-picker
              style="width: 100%;"
              v-model="commission.toDate"
              type="date"
              :placeholder="t('to')"
              size="large"
              :popper-class="$ctx.getDir() === 'rtl' ? 'rtl' : ''"
          >
          </el-date-picker>
        </el-form-item>
      </div>
    </input-section>

    <input-section>
      <el-form-item prop="disabled" class="input-label" style="margin: 0 auto">
        <el-checkbox v-model="commission.disabled" id="disabled">
          {{ t('disabled') }}
        </el-checkbox>
      </el-form-item>
    </input-section>

  </el-form>
</template>

<script lang="ts">
import {defineComponent, onMounted, ref} from 'vue'

// components
import AlertBadge from '@/components/AlertBadge.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'

// models
import MoneyCollectorCommission, {
  moneyCollectorCommissionI18nMessages,
  CollectionRule
} from '@/models/hr/MoneyCollectorCommission'
import {useI18n} from 'vue-i18n'
import {ElDatePicker, ElForm} from 'element-plus'
import User from '@/models/user/User'
import Location from '@/models/company/Location'
import {useHTTP} from '@/plugins/http'
import draggable from 'vuedraggable'
import MoneyInput from '@/components/form/MoneyInput.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

export default defineComponent({
  name: 'money-collector-commission-form',
  components: {
    LoadingSpinner,
    MoneyInput,
    ElDatePicker,
    AlertBadge,
    HorizontalButton,
    InputSection,
    draggable
  },
  props: {
    initCommission: {
      type: MoneyCollectorCommission,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      required: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...moneyCollectorCommissionI18nMessages.en,
        },
        ar: {
          ...moneyCollectorCommissionI18nMessages.ar,
        }
      },
      useScope: 'global'
    })

    const http = useHTTP()

    const locations = ref<Array<Location>>()
    const moneyCollectors = ref<Array<User>>()
    const periods = ref([
      'open'
      // 'date_range' - Hidden as per requirements
    ])

    const loading = ref(true)
    const error = ref({title: '', body: ''})

    onMounted(async () => {
      try {
        const locationsResp = await http.get<Location[]>(Location.ENDPOINT)
        locations.value = locationsResp.map((data) => Location.from(data))

        // Fetch users with "Money Collector" tag
        const employeesRes = await http.get<User[]>(User.ENDPOINT)
        moneyCollectors.value = employeesRes
          .map((data) => User.from(data))
          .filter((user) => user.extraProperties?.tags?.includes('Money Collector'))

        loading.value = false
      } catch (e: any) {
        console.log(e)
        error.value.title = e.title
        error.value.body = e.body
      }
    })

    return {
      t,
      locations,
      moneyCollectors,
      periods,
      loading,
      error
    }
  },

  data() {
    return {
      commission: this.initCommission,
      error: {title: '', body: ''},
      rules: {
        name: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          },
          {
            max: 255,
            message: this.$t('validation.maxLength', {len: 255}),
            trigger: 'blur'
          }
        ],
      }
    }
  },

  methods: {
    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
    },

    async validateSubmit(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        }

        const resp = await this.submitAction(this.commission)

        if (resp) {
          this.error.title = resp?.title
          this.error.body = resp?.body
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        } else {
          if (this.clearFormOnSubmit) {
            this.clearForm(formName)
          }

          this.$alertModal.showSuccess({title: this.successMsg, body: ''})
        }
        return true
      })
    },

    addRule() {
      if (!this.commission.rules) {
        this.commission.rules = []
      }
      this.commission.rules?.push(new CollectionRule())
    },

    reorder() {
      this.commission.rules?.forEach((rule, index) => (rule.order = index))
    },

    deleteRule(rule: CollectionRule, index: number) {
      this.commission.rules?.splice(index, 1)
      this.reorder()
    }
  }
})
</script>

<style scoped>
form > h1 {
  @apply text-lg py-3 font-bold;
}

form > h2 {
  @apply py-3 font-bold;
}

formt > h3 {
  @apply py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}

.custom-input-number {
  width: 100%;
}

.custom-input-number :deep(.el-input__inner) {
  text-align: left;
}

.select-all :deep(.el-select__selected-item) {
  display: none;
}
</style>
